import { InputBaseComponentProps, TextField, TextFieldProps } from '@mui/material';
import * as React from 'react';
import { FieldInputProps, FieldMetaState } from 'react-final-form';
import { NumericFormatProps, SourceInfo } from 'react-number-format';
import useMoney from '../../hooks/useMoney';
import { useTranslation } from '../../modules/translation/TranslationContext';
import teceoInputConfig from '../../utils/TeceoInputConfig';
import NumberFormatCustom, { NumberFormatCustomOnChange } from '../NumberFormatCustom';

type NumberTextFieldProps<
  Input = FieldInputProps<string>,
  Meta = FieldMetaState<string>,
> = TextFieldProps & {
  decimalScale?: number;
  input?: Input;
  meta?: Meta;
};

const NumberFormat: React.ElementType<InputBaseComponentProps> = ({
  inputRef,
  onChange,
  decimalScale = 0,
  ...rest
}) => {
  const { decimalSeparator, thousandSeparator } = useMoney();

  const numericFormatProps = rest as unknown as NumericFormatProps;
  const handleChange = onChange as unknown as ((
    params: NumberFormatCustomOnChange,
    event: SourceInfo['event'],
  ) => void) &
    React.ChangeEventHandler<HTMLInputElement>;

  return (
    <NumberFormatCustom
      {...numericFormatProps}
      getInputRef={inputRef}
      onChange={handleChange}
      decimalScale={decimalScale}
      fixedDecimalScale
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
    />
  );
};

const NumberTextField = React.forwardRef(
  ({ input, meta, InputProps, slotProps, decimalScale, ...props }: NumberTextFieldProps, ref) => {
    const { translate } = useTranslation();
    const inputSlotProps = typeof slotProps?.input === 'function' ? {} : slotProps?.input;

    return (
      <TextField
        {...props}
        {...input}
        {...teceoInputConfig}
        error={(meta?.touched && !!meta?.error) || props.error}
        helperText={
          props.helperText ||
          (meta?.touched && !!meta?.error && translate(meta?.error, {}, meta?.error))
        }
        slotProps={{
          ...slotProps,
          inputLabel: { shrink: input?.value !== '' || undefined, ...slotProps?.inputLabel },
          input: {
            ...InputProps,
            className: inputSlotProps?.className ?? InputProps?.className,
            endAdornment: inputSlotProps?.endAdornment ?? InputProps?.endAdornment,
            inputRef: ref,
            inputProps: { decimalScale },
            inputComponent: NumberFormat,
          },
        }}
      />
    );
  },
);

export default React.memo(NumberTextField);
